import type { UnitType, ConfigType, OpUnitType } from 'dayjs';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import utc from 'dayjs/plugin/utc';

import 'dayjs/locale/de';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(updateLocale);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(utc);

export type DateType = Dayjs;

export type DateRange = {
  start?: DateType | null;
  end?: DateType | null;
  startDate?: number | null;
  endDate?: number | null;
};

const FORMAT = {
  DEFAULT: 'L',
  DATE_WITH_FULL_MONTH: 'LL',
  FULL_MONTH_NAME_WITH_YEAR: 'MMMM YYYY',
  LOCAL_TIME: 'LT',
  YEAR: 'YYYY',
};

dayjs().localeData();

// add date format for de
dayjs.updateLocale('de', {
  weekStart: 1,
});

/**
 * Convert date to date object.
 *
 * @param d - date
 *
 * @returns {DateType}
 */
const date = (d?: ConfigType): Dayjs => {
  return dayjs(d).utc();
};

/**
 * Change date util to passed language.
 *
 * @param lang - new locale
 */
const changeLocale = (lang: string) => {
  dayjs.locale(lang);
};

/**
 * Print passed timestamp in human-readable format.
 *
 * @see https://day.js.org/docs/en/display/format
 *
 * @type {string}
 */
const printDate = (timestamp: number): string => {
  const d = date(timestamp);
  const diff = date().diff(timestamp, 'day');

  if (diff > 3) {
    return d.format(FORMAT.DEFAULT);
  }

  return d.fromNow();
};

/**
 * Print passed timestamp in human-readable format with time.
 *
 * @see https://day.js.org/docs/en/display/format
 *
 * @type {string}
 */
const printDateWithTime = (timestamp: number): string => {
  const d = date(timestamp);
  const diff = date().diff(timestamp, 'day');

  if (diff > 3) {
    return `${d.format(FORMAT.DEFAULT)}, ${d.format('LTS')}`;
  } else if (diff > 1) {
    return `${d.fromNow()}, ${d.format('LTS')}`;
  } else {
    return d.fromNow();
  }
};

/**
 * Return date in locale specific format
 *
 * @param date - date object
 * @param format - format of result
 *
 * @returns {string}
 */
const formatDate = (date: DateType, format: string = FORMAT.DEFAULT): string => {
  return date.format(format);
};

/**
 * Return string with start & end date.
 *
 * @param start - start date
 * @param end - end date
 *
 * @returns {string}
 */
const formatDateRange = ({ start, end }: DateRange): string => {
  if (start == null) {
    return '';
  }

  let result = formatDate(start);
  // Non-breaking space to avoid an overflowing third line
  result += ' - ';

  if (end != null) {
    result += formatDate(end);
  }

  return result;
};

/**
 * Return name of the month
 *
 * @param date - date object
 *
 * @returns {string}
 */
const getMonthName = (date: DateType): string => {
  const idx = date.month();
  return dayjs.months()[idx];
};

/**
 * Return names of the week.
 *
 * @returns {Array<string>}
 */
const getDayNames = (): Array<string> => {
  return dayjs.weekdays();
};

/**
 * Returns true if given date is inside the range.
 *
 * @param date - date to check
 * @param range - date range
 * @param granularity - granularity
 *
 * @returns {boolean}
 */
const isInRange = (date: DateType, range: DateRange, granularity?: OpUnitType | null): boolean => {
  if (range.start == null || range.end == null) {
    return false;
  }

  return date.isBetween(range.start, range.end, granularity, '[]');
};

/**
 * Check if date is the same.
 *
 * @param date - date object
 * @param toCompare - date to compare with
 * @param granularity - granularity
 *
 * @returns {boolean}
 */
const isSame = (date: Dayjs, toCompare: ConfigType, granularity?: UnitType): boolean => {
  return date.isSame(toCompare, granularity);
};

/**
 * Return date & optionally include time.
 *
 * @param timestamp - timestamp
 * @param includeTime - true to add time
 *
 * @returns {string}
 */
const formatFullDate = (timestamp: number = 0, includeTime: boolean): string => {
  const d = date(timestamp);
  let fullDateString = formatDate(d);

  if (includeTime) {
    fullDateString += `, ${d.format(FORMAT.LOCAL_TIME)}`;
  }

  return fullDateString;
};

// return utils
export const DateUtil = {
  FORMAT,
  date,
  changeLocale,
  formatDate,
  formatDateRange,
  getMonthName,
  getDayNames,
  printDate,
  printDateWithTime,
  isInRange,
  isSame,
  formatFullDate,
};
